import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type StickerGradient = {
  coords: { x1: string; y1: string; x2: string; y2: string };
  stops: ReactNode;
};

const parseViewBox = (viewBox: string) => {
  const [x, y, width, height] = viewBox.split(/\s+/).map(Number);
  return { x, y, width, height };
};

type Props = {
  /** `d` of the sticker outline, expressed in `viewBox` units */
  shape: string;
  viewBox: string;
  height: number;
  fitBoxToPaintedContent?: boolean;
  fill: string | StickerGradient;
  textColor: string;
  /** Halo painted with `fill` behind the text, so glyphs can sit outside `shape` */
  textOutlineWidth?: number;
  title?: ReactNode;
  /** `<text>` elements positioned in `viewBox` units; they inherit their paint */
  children: ReactNode;
  className?: string;
};

export const Sticker = ({
  shape,
  viewBox,
  height,
  fitBoxToPaintedContent,
  fill,
  textColor,
  textOutlineWidth = 0,
  title,
  children,
  className,
}: Props) => {
  const id = useId();
  const contentId = `${id}-content`;
  const maskId = `${id}-mask`;
  const gradient = typeof fill === 'string' ? null : fill;
  const paint = typeof fill === 'string' ? fill : `url(#${id})`;

  const inkRef = useRef<SVGGElement>(null);
  const [painted, setPainted] = useState<{ x: number; width: number } | null>(
    null
  );

  useLayoutEffect(() => {
    if (!fitBoxToPaintedContent || !inkRef.current) return;

    const ink = inkRef.current.getBBox();
    const halo = textOutlineWidth / 2;
    setPainted({ x: ink.x - halo, width: ink.width + textOutlineWidth });
  }, [fitBoxToPaintedContent, textOutlineWidth, children]);

  const box = parseViewBox(viewBox);
  const scale = height / box.height;
  const fitted = painted ?? { x: box.x, width: box.width };
  const bleed = box.height;

  return (
    <svg
      className={className}
      width={fitted.width * scale}
      height={height}
      viewBox={`${fitted.x} ${box.y} ${fitted.width} ${box.height}`}
      fill="none"
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-labelledby={`${id}-title`}
      style={{ fontFamily: 'var(--pressio-font)' }}
    >
      <title id={`${id}-title`}>{title}</title>

      <defs>
        {!!gradient && (
          <linearGradient
            id={id}
            {...gradient.coords}
            gradientUnits="userSpaceOnUse"
          >
            {gradient.stops}
          </linearGradient>
        )}

        <g id={contentId}>{children}</g>

        {textOutlineWidth > 0 && (
          <mask id={maskId}>
            <use
              href={`#${contentId}`}
              fill="white"
              stroke="white"
              strokeWidth={textOutlineWidth}
            />
          </mask>
        )}
      </defs>

      {textOutlineWidth > 0 && (
        <rect
          x={fitted.x - bleed}
          y={box.y - bleed}
          width={fitted.width + bleed * 2}
          height={box.height + bleed * 2}
          fill={paint}
          mask={`url(#${maskId})`}
        />
      )}

      <g ref={inkRef}>
        <path d={shape} fill={paint} />
        <use href={`#${contentId}`} fill={textColor} />
      </g>
    </svg>
  );
};
